import { View, Platform, ScrollView, Modal, Pressable, TextInput, Alert } from 'react-native';
import { Text } from '@constants/Themed';
import LottieView from 'lottie-react-native';
import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from '@stylesheet';
import { useLottieAnim } from '@hooks/lottieHook';
import menuStates from './menuStates';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { Foundation, FontAwesome, Entypo, AntDesign, MaterialIcons } from '@expo/vector-icons';
import { NavigationHelpers, useNavigation } from "@react-navigation/native";
import { DashboardNavigatorParamList, StackParamList } from '@navigation/types';
import { useTranslation } from 'react-i18next';
import { HOST } from '@api/constant';
import { updateApiInstances, api } from '@api/api';
import apis from '@api/api';
import { XStack } from 'tamagui';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { ComingSoonModal, useComingSoonModal } from '@components/Global/ComingSoon.index';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEnhancedSubscriptionStatus } from '@hooks/useSubscriptionStatus';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { SKIP_ONBOARDING } from '@reduxLocal/actions/types';
interface StaticMenuHeaderProps {
  messageCount?: number;
  callBack?: () => void;
  invitationCount?: number;
}

// React Native Badge Component
interface BadgeProps {
  badgeContent?: number;
  children: React.ReactNode;
  showZero?: boolean;
  max?: number;
  color?: 'error' | 'primary';
}

const Badge: React.FC<BadgeProps> = ({ badgeContent = 0, children, showZero = false, max = 99, color = 'error' }) => {
  const shouldShow = badgeContent > 0 || (showZero && badgeContent === 0);
  const displayCount = badgeContent > max ? `${max}+` : badgeContent;

  return (
    <View style={{ position: 'relative' }}>
      {children}
      {shouldShow && (
        <View style={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: color === 'error' ? '#f44336' : '#2196f3',
          borderRadius: 10,
          paddingHorizontal: 6,
          paddingVertical: 2,
          minWidth: 20,
          alignItems: 'center',
          maxHeight: 20,
          justifyContent: 'center',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.3,
          shadowRadius: 4,
          elevation: 4,
          borderWidth: 1,
          overflow: "visible",
          borderColor: 'white',
          zIndex: 10,
        }}>
          <Text style={{
            color: 'white',
            fontSize: 10,
            top: -6,
            minHeight: 20,
            fontWeight: 'bold',
            textAlign: 'center',
          }}>
            {displayCount}
          </Text>
        </View>
      )}
    </View>
  );
};

const StaticMenuHeader = React.memo(({ messageCount = 0, callBack, invitationCount = 0 }: StaticMenuHeaderProps) => {
  // Initialize animation refs first
  const animation1 = useLottieAnim();
  const animation2 = useLottieAnim();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile?.id);
  // Use simple, stable state access
  const theme = useSelector((state: DefaultRootState) => {
    return state?.app?.theme?.lightTheme !== false; // Default to true
  });
  const { showAlert } = useAlertDialog();
  const navigation = useNavigation<NavigationHelpers<StackParamList>>();

  const menuOpen = useSelector((state: DefaultRootState) => {
    return Platform.select({ web: state?.UI?.menuOpen === true, default: true });
  });

  // Simplified user selector  
  const user = useSelector((state: DefaultRootState) => {
    return state?.promiseData?.data?.userData;
  });

  const userId = currentCompany || user?.id;
  const { t } = useTranslation();

  // Use enhanced subscription hook (it will use existing React Query cache)
  const subscription = useEnhancedSubscriptionStatus();
  // Select onboarding state from Redux - will automatically update when SKIP_ONBOARDING is dispatched
  // from any component (including EasyModeScreen)
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);
  const dispatch = useDispatch();
  const { showConfirm } = useAlertDialog();

  // Store menu state
  const [storeMenuAnchor, setStoreMenuAnchor] = useState<null | HTMLElement>(null);
  const storeButtonRef = useRef<View>(null);
  const [storeButtonLayout, setStoreButtonLayout] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  // Development-only API URL configuration state
  const [devApiUrlModalVisible, setDevApiUrlModalVisible] = useState(false);
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [currentApiUrl, setCurrentApiUrl] = useState(HOST);
  const [urlError, setUrlError] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(true);

  // Load saved API URL on component mount (development only)
  useEffect(() => {
    if (__DEV__) {
      loadSavedApiUrl();
      // Initialize API instances with saved URL
      updateApiInstances();
    }
  }, []);

  const loadSavedApiUrl = async () => {
    try {
      const savedUrl = await AsyncStorage.getItem('dev_custom_api_url');
      if (savedUrl) {
        setCurrentApiUrl(savedUrl);
        setCustomApiUrl(savedUrl);
      }
    } catch (error) {
      console.warn('Failed to load saved API URL:', error);
    }
  };

  // URL validation function
  const validateUrl = (url: string): { isValid: boolean; error: string } => {
    if (!url.trim()) {
      return { isValid: false, error: 'Please enter a URL' };
    }

      // Check if URL starts with http:// or https://
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return { isValid: false, error: 'URL must start with http:// or https://' };
      }

      try {
        new URL(url);
        return { isValid: true, error: '' };
      } catch {
        return { isValid: false, error: 'Please enter a valid URL format' };
      }
    };

  // Handle URL input change with validation
  const handleUrlChange = (text: string) => {
    setCustomApiUrl(text);
    const validation = validateUrl(text);
    setIsValidUrl(validation.isValid);
    setUrlError(validation.error);
  };

  const handleDevApiUrlSave = useCallback(async () => {
    const validation = validateUrl(customApiUrl);

      if (!validation.isValid) {
        Alert.alert('Invalid URL', validation.error);
        return;
      }

      try {
        // Test the API connection first
        const isConnected = await testApiConnection(customApiUrl);

        await AsyncStorage.setItem('dev_custom_api_url', customApiUrl);
        setCurrentApiUrl(customApiUrl);

        // Update API instances immediately
        await updateApiInstances(customApiUrl);

        // Debug: Log the current API instance baseURLs
        console.log('🔍 API instance baseURLs after update:');
        console.log('api.defaults.baseURL:', api.defaults.baseURL);
        console.log('Expected URL:', `${customApiUrl}/api`);

        setDevApiUrlModalVisible(false);
        setUrlError('');
        setIsValidUrl(true);

        // Show success message with connection status
        const connectionStatus = isConnected ? '✅ Connection successful' : '⚠️ Connection test failed';
        Alert.alert(
          'Success',
          `API URL updated to:\n${customApiUrl}\n\n${connectionStatus}\n\nAll API requests will now use this URL.`,
          [
            { text: 'OK', style: 'default' }
          ]
        );
      } catch (error) {
        Alert.alert('Error', 'Failed to save API URL. Please try again.');
      }
    }, [customApiUrl]);

  const handleDevApiUrlReset = useCallback(async () => {
    try {
      await AsyncStorage.removeItem('dev_custom_api_url');
      setCurrentApiUrl(HOST);
      setCustomApiUrl('');
      setUrlError('');
      setIsValidUrl(true);

        // Update API instances to use default URL
        await updateApiInstances(HOST);

        setDevApiUrlModalVisible(false);
        Alert.alert('Success', 'API URL reset to default. All API requests will now use the default URL.');
      } catch (error) {
        Alert.alert('Error', 'Failed to reset API URL');
      }
    }, []);

  // Handle opening the modal
  const handleOpenDevApiModal = useCallback(() => {
    setCustomApiUrl(currentApiUrl);
    setUrlError('');
    setIsValidUrl(true);
    setDevApiUrlModalVisible(true);
  }, [currentApiUrl]);

  // Test API connection using fetch with proper CORS handling
  const testApiConnection = useCallback(async (url: string) => {
    try {
      // Use fetch with no-cors mode to avoid CORS issues during testing
      const response = await fetch(`${url}/api/health`, {
        method: 'GET',
        mode: 'no-cors', // This bypasses CORS for testing
        cache: 'no-cache',
      });

        // With no-cors mode, we can't read the response, but if it doesn't throw, it means the server responded
        return true;
      } catch (error) {
        console.warn('API connection test failed:', error);
        return false;
      }
    }, []);

  //Coming Soon modal for Marketplace
  const { modalState: marketplaceModalState, showModal: showMarketplaceModal, hideModal: hideMarketplaceModal } = useComingSoonModal({
    title: "SmartSaaS Marketplace",
    subtitle: "Discover, buy, and sell digital products",
    message: "Our marketplace is coming soon! Get ready to explore a wide range of digital products, from templates to plugins, and take your SmartSaaS experience to the next level.",
    showAnimation: true,
  });

  // Memoize the modal configuration to prevent unnecessary re-renders
  const academyModalConfig = useMemo(() => ({
    title: "SmartSaaS Academy",
    subtitle: "Learn, grow, and master your business",
    message: "Our comprehensive learning platform is coming soon! Get ready for interactive courses, expert tutorials, certification programs, and personalized learning paths to help you maximize your SmartSaaS experience and grow your business.",
    showAnimation: true,
  }), []);

  // Coming Soon modal for Academy
  const { modalState: academyModalState, showModal: showAcademyModal, hideModal: hideAcademyModal } = useComingSoonModal(academyModalConfig);

  // Store menu handlers
  const handleStoreMenuOpen = useCallback((event: any) => {
    if (Platform.OS === 'web') {
      setStoreMenuAnchor(event.currentTarget);
    } else {
      // For mobile, set anchor first to ensure modal can render
      setStoreMenuAnchor(event.currentTarget);

      // Measure button position - use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (storeButtonRef.current) {
            try {
              storeButtonRef.current.measureInWindow((x, y, width, height) => {
                if (x !== undefined && y !== undefined) {
                  setStoreButtonLayout({ x, y, width: width || 0, height: height || 0 });
                } else {
                  // Fallback if measurement returns undefined
                  setStoreButtonLayout({ x: 20, y: 150, width: 0, height: 0 });
                }
              });
            } catch (error) {
              console.warn('Failed to measure store button position:', error);
              // Use fallback position
              setStoreButtonLayout({ x: 20, y: 150, width: 0, height: 0 });
            }
          } else {
            // Use fallback position if ref not available
            setStoreButtonLayout({ x: 20, y: 150, width: 0, height: 0 });
          }
        });
      });
    }
  }, []);

  const handleStoreMenuClose = useCallback(() => {
    setStoreMenuAnchor(null);
    setStoreButtonLayout(null);
  }, []);

  // Marketplace button handler
  const handleMarketplacePress = useCallback(async () => {
    if (onboardingInProgress) {
      const result = await showConfirm(
        'Skip Onboarding?',
        'You are currently in the onboarding process. This action will skip the onboarding. Do you want to continue?',
        {
          cancelText: 'Cancel',
          confirmText: 'Skip'
        }
      );

      if (result !== true) {
        return;
      }

      // Update backend to mark tutorial modal as complete
      try {
        await apis.setTutorial();
      } catch (error) {
        console.error('Error setting tutorial complete:', error);
        // Continue even if API call fails - still update Redux
      }

      // Update Redux state
      dispatch({ type: SKIP_ONBOARDING });
    }
    showAlert('Marketplace coming soon!', 'Shortly, you will be able to trade your services on SmartSaaS as a freelancer / contractor. Keep an eye out for updates!');
  }, [onboardingInProgress, showConfirm, showAlert, dispatch]);

  // Helper function to handle navigation with onboarding check
  const handleNavigationWithOnboardingCheck = useCallback(async (navigationFn: () => void, screenName: string) => {
    if (onboardingInProgress) {
      const result = await showConfirm(
        'Skip Onboarding?',
        `You are currently in the onboarding process. Navigating to ${screenName} will skip the onboarding. Do you want to continue?`,
        {
          cancelText: 'Cancel',
          confirmText: 'Skip & Navigate'
        }
      );
      
      if (result !== true) {
        return;
      }

      // Update backend to mark tutorial modal as complete
      try {
        await apis.setTutorial();
      } catch (error) {
        console.error('Error setting tutorial complete:', error);
        // Continue even if API call fails - still update Redux
      }

      // Update Redux state
      dispatch({ type: SKIP_ONBOARDING });
    }
    navigationFn();
  }, [onboardingInProgress, showConfirm, dispatch]);

  const handleStoreAction = useCallback(async (action: string) => {
    // Early return if no userId available
    if (!userId) {
      console.warn('Cannot perform store action: user ID not available');
      return;
    }

    // Check onboarding for navigation actions
    if (onboardingInProgress && (action === 'visit' || action === 'settings')) {
      const screenName = action === 'visit' ? 'Store' : 'Store Settings';
      const result = await showConfirm(
        'Skip Onboarding?',
        `You are currently in the onboarding process. Navigating to ${screenName} will skip the onboarding. Do you want to continue?`,
        {
          cancelText: 'Cancel',
          confirmText: 'Skip & Navigate'
        }
      );
      
      if (result !== true) {
        return;
      }

      // Update backend to mark tutorial modal as complete
      try {
        await apis.setTutorial();
      } catch (error) {
        console.error('Error setting tutorial complete:', error);
        // Continue even if API call fails - still update Redux
      }
      
      // Update Redux state
      dispatch({ type: SKIP_ONBOARDING });
    }

      switch (action) {
        case 'share':
          // Implement share store functionality
          if (Platform.OS === 'web') {
            if (navigator.share) {
              navigator.share({
                title: t('share-store-title') || 'Check out my store!',
                text: t('share-store-text') || 'Visit my SmartSaaS store',
                url: `${HOST}/store?uid=${userId}`,
              });
            } else {
              navigator.clipboard.writeText(`${HOST}/store?uid=${userId}`);
              // You could add a toast notification here
            }
          }
          break;
        case 'visit':
          // Visit store functionality (existing behavior)
          if (Platform.OS === 'web') {
            window.open(`${HOST}/store?uid=${userId}`, '_blank');
          } else { 
            navigation.navigate('Store');
          }
          break;
        case 'settings':
          // Navigate to store settings
          navigation.replace('Settings', 
            {
              screen: 'StoreSettings',
            });
          break;
      }
      handleStoreMenuClose();
  }, [String(userId || ''), navigation, t, onboardingInProgress, showConfirm, dispatch, handleStoreMenuClose]); // Convert to string to ensure primitive dependency

  // Development API URL Modal Component
  const DevApiUrlModal = useCallback(() => {
    if (!__DEV__) return null;

      return (
        <Modal
          transparent={true}
          visible={devApiUrlModalVisible}
          onRequestClose={() => setDevApiUrlModalVisible(false)}
          animationType="slide"
        >
          <Pressable onPress={() => setDevApiUrlModalVisible(false)}>
            <View style={{
              flex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              justifyContent: 'center',
              alignItems: 'center',
              paddingHorizontal: 20,
            }}>
              <Pressable onPress={() => { }}>
                <View style={{
                  backgroundColor: theme ? '#ffffff' : '#333333',
                  borderRadius: 12,
                  padding: 24,
                  width: '100%',
                  maxWidth: 400,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 8,
                  elevation: 8,
                  borderWidth: 1,
                  borderColor: theme ? '#e0e0e0' : '#555555',
                }}>
                  <Text style={{
                    fontSize: 18,
                    fontWeight: 'bold',
                    color: theme ? '#333' : '#fff',
                    marginBottom: 16,
                    textAlign: 'center',
                  }}>
                    🔧 Development API URL Configuration
                  </Text>

                  <Text style={{
                    fontSize: 14,
                    color: theme ? '#666' : '#ccc',
                    marginBottom: 8,
                  }}>
                    Current API URL:
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#888' : '#aaa',
                    marginBottom: 16,
                    fontFamily: 'monospace',
                  }}>
                    {currentApiUrl}
                  </Text>

                  <Text style={{
                    fontSize: 14,
                    color: theme ? '#666' : '#ccc',
                    marginBottom: 8,
                  }}>
                    New API URL:
                  </Text>
                  <TextInput
                    style={{
                      borderWidth: 2,
                      borderColor: isValidUrl ? (theme ? '#ddd' : '#555') : '#ff4444',
                      borderRadius: 8,
                      padding: 12,
                      fontSize: 14,
                      color: theme ? '#333' : '#fff',
                      backgroundColor: theme ? '#fff' : '#444',
                      marginBottom: urlError ? 8 : 16,
                      fontFamily: 'monospace',
                    }}
                    placeholder="https://api.example.com"
                    placeholderTextColor={theme ? '#999' : '#666'}
                    value={customApiUrl}
                    onChangeText={handleUrlChange}
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="url"
                  />

                  {urlError ? (
                    <Text style={{
                      fontSize: 12,
                      color: '#ff4444',
                      marginBottom: 16,
                      fontStyle: 'italic',
                    }}>
                      ⚠️ {urlError}
                    </Text>
                  ) : null}

                  <View style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}>
                    <Pressable
                      onPress={handleDevApiUrlReset}
                      style={{
                        flex: 1,
                        backgroundColor: theme ? '#f5f5f5' : '#555',
                        paddingVertical: 12,
                        paddingHorizontal: 16,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: theme ? '#ddd' : '#666',
                      }}
                    >
                      <Text style={{
                        color: theme ? '#666' : '#ccc',
                        textAlign: 'center',
                        fontWeight: '500',
                      }}>
                        Reset
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={() => setDevApiUrlModalVisible(false)}
                      style={{
                        flex: 1,
                        backgroundColor: theme ? '#f5f5f5' : '#555',
                        paddingVertical: 12,
                        paddingHorizontal: 16,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: theme ? '#ddd' : '#666',
                      }}
                    >
                      <Text style={{
                        color: theme ? '#666' : '#ccc',
                        textAlign: 'center',
                        fontWeight: '500',
                      }}>
                        Cancel
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={handleDevApiUrlSave}
                      disabled={!isValidUrl || !customApiUrl.trim()}
                      style={{
                        flex: 1,
                        backgroundColor: (!isValidUrl || !customApiUrl.trim()) ? '#ccc' : '#5ac0ea',
                        paddingVertical: 12,
                        paddingHorizontal: 16,
                        borderRadius: 8,
                        opacity: (!isValidUrl || !customApiUrl.trim()) ? 0.6 : 1,
                      }}
                    >
                      <Text style={{
                        color: '#fff',
                        textAlign: 'center',
                        fontWeight: '600',
                      }}>
                        Save
                      </Text>
                    </Pressable>
                  </View>
                </View>
              </Pressable>
            </View>
          </Pressable>
        </Modal>
      );
    }, [devApiUrlModalVisible, theme, currentApiUrl, customApiUrl, handleDevApiUrlSave, handleDevApiUrlReset]);

  // Store menu dropdown component - memoized to prevent unnecessary re-renders
  const StoreMenuDropdown = useMemo(() => {
    const backdropStyle = Platform.select({
      web: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'flex-start' as const,
        alignItems: 'flex-start' as const,
        paddingTop: 80,
        paddingLeft: 20,
      },
      default: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      },
    });

    const dropdownStyle = Platform.select({
      web: {},
      default: storeButtonLayout ? {
        position: 'absolute' as const,
        top: storeButtonLayout.y + storeButtonLayout.height + 8,
        left: Math.max(10, storeButtonLayout.x), // Align to button's left edge, with minimum margin
      } : {
        // Fallback positioning if measurement hasn't completed yet
        position: 'absolute' as const,
        top: 150,
        left: 20,
      },
    });

    // Prevent dropdown from closing when clicking inside it
    const handleDropdownPress = (e: any) => {
      e.stopPropagation();
    };

    return (
      <Modal
        transparent={true}
        visible={Boolean(storeMenuAnchor)}
        onRequestClose={handleStoreMenuClose}
        animationType="fade"
      >
        <View style={backdropStyle}>
          <Pressable
            onPress={handleStoreMenuClose}
            style={{ flex: 1 }}
          />
          <Pressable
            onPress={handleDropdownPress}
            style={{
              zIndex: 10,
              position: 'absolute' as const,
            }}
          >
            <View
              style={[
                {
                  backgroundColor: theme ? '#ffffff' : '#333333',
                  borderRadius: 8,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 8,
                  elevation: 8,
                  paddingVertical: 8,
                  minWidth: 180,
                  borderWidth: 1,
                  borderColor: theme ? '#e0e0e0' : '#555555',
                },
                dropdownStyle,
              ]}
              onStartShouldSetResponder={() => true}
              onMoveShouldSetResponder={() => true}
            >
                <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  handleStoreAction('share');
                }}
                  style={({ pressed }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    backgroundColor: pressed ? (theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)') : 'transparent',
                  })}
                >
                  <MaterialIcons name="share" size={18} color={theme ? '#333' : '#fff'} style={{ marginRight: 12 }} />
                  <Text style={{ color: theme ? '#333' : '#fff', fontSize: 14 }}>
                    {t('share-store') || 'Share Store'}
                  </Text>
                </Pressable>

                  <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  handleStoreAction('visit');
                }}
                    style={({ pressed }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      backgroundColor: pressed ? (theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)') : 'transparent',
                    })}
                  >
                    <MaterialIcons name="store" size={18} color={theme ? '#333' : '#fff'} style={{ marginRight: 12 }} />
                    <Text style={{ color: theme ? '#333' : '#fff', fontSize: 14 }}>
                      {t('visit-store') || 'Visit Store'}
                    </Text>
                  </Pressable>

                  <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  handleStoreAction('settings');
                }}
                    style={({ pressed }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      backgroundColor: pressed ? (theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)') : 'transparent',
                    })}
                  >
                    <MaterialIcons name="settings" size={18} color={theme ? '#333' : '#fff'} style={{ marginRight: 12 }} />
                    <Text style={{ color: theme ? '#333' : '#fff', fontSize: 14 }}>
                      {t('store-settings') || 'Store Settings'}
                    </Text>
                  </Pressable>
                </View>
              </Pressable>
        </View>
      </Modal>
    );
  }, [storeMenuAnchor, storeButtonLayout, theme, handleStoreMenuClose, handleStoreAction, t]);

  // Safely check if user is premium with proper null checking
  const isPremiumUser = useCallback(() => {
    if (!user) return false;

      // Check multiple premium indicators with safe access
      const isPremium = user?.isPremium === true;
      const hasActiveSubscription = user?.subscriptionStatus === 'active';
      const hasBillingActive = user?.billing?.isActive === true;

      return isPremium || hasActiveSubscription || hasBillingActive;
    }, [
      String(user?.id || ''),                    // Convert to string
      Boolean(user?.isPremium),                  // Convert to boolean
      String(user?.subscriptionStatus || ''),   // Convert to string
      Boolean(user?.billing?.isActive)          // Convert to boolean
    ]); // Use primitive values to ensure stable dependencies

  // Check if user is in trial mode
  const isInTrial = useMemo(() => {
    if (!subscription.subscriptionPlan) return false;

    // Check subscriptionPlan data structure
    const subscriptionData = subscription.subscriptionPlan?.data?.data?.[0] || subscription.subscriptionPlan?.data?.[0] || subscription.subscriptionPlan;

    // Check explicit trialing status
    if (subscriptionData?.status === 'trialing') return true;

    // Also check if trial_end exists and is in the future
    if (subscriptionData?.trial_end) {
      const trialEndDate = new Date(subscriptionData.trial_end * 1000);
      const now = new Date();
      if (trialEndDate > now) {
        return true; // Still in trial period
      }
    }

    return false;
  }, [
    subscription.subscriptionPlan?.data?.data?.[0]?.status,
    subscription.subscriptionPlan?.data?.data?.[0]?.trial_end,
    subscription.subscriptionPlan?.data?.[0]?.status,
    subscription.subscriptionPlan?.data?.[0]?.trial_end,
    subscription.subscriptionPlan?.status,
    subscription.subscriptionPlan?.trial_end,
  ]);

  return (
    <View style={[
      styles.staticMenuHeader,
      theme ? styles.staticMenuHeaderLight : styles.staticMenuHeaderDark
      , {
        elevation: 0,
          left: Platform.select({ web: menuOpen ? undefined : 10, default: 10 }),
          top: Platform.select({ web: undefined, default: -30 }),
          margin: 5, borderTopStartRadius: 0, borderBottomStartRadius: 0,
          paddingRight: Platform.select({ native: 5 }),
          maxWidth: "90%",
          backgroundColor: Platform.select({ ios: undefined, android: undefined, web: 'transparent' })
        }]}>
        <XStack
          style={[
            styles.staticMenuXStack,
            {
              flexDirection: menuOpen ? "row" : "column",
              padding: 20,
              minWidth: Platform.select({ native: "110%", web: undefined }),
              columnGap: Platform.select({ web: 10, native: 10 }),
              maxWidth: Platform.select({ web: undefined, default: 300 }),
              flexWrap: 'wrap',
              left: Platform.select({ web: !menuOpen && -30, default: -20 })
              // Note: columnGap and flexWrap might not be supported in all React Native versions
              // Using margin on child elements instead for spacing
            }
          ]}
        >
          <Pressable
          onPress={() => handleNavigationWithOnboardingCheck(() => navigation.replace('Mail'), 'Mail')}
            onHoverIn={() => { animation1.current?.reset(), animation1.current?.play() }}
            style={({ pressed, hovered }) => ([
              styles.centerEverything,
              styles.staticMenuPressable,
              menuOpen ? styles.staticMenuPressableOpen : styles.staticMenuPressableClosed,
              (pressed || hovered) && (theme ? styles.staticMenuPressableHoveredLight : styles.staticMenuPressableHoveredDark),
              Platform.select({
                ios: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 4,
                },
                android: {
                  elevation: 0,
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                },
              }),
            ])}
          >
            <View style={[
            styles.staticMenuIconContainer,
            ]}>
              <Badge color="error" showZero={true} max={999} badgeContent={messageCount}>
                <View style={[
                  styles.staticMenuIconWrapper,
                  theme ? styles.staticMenuIconWrapperLight : styles.staticMenuIconWrapperDark,
                  Platform.select({
                    ios: { padding: 10 },
                    android: { padding: 10 },
                  })
                ]}>
                  <FontAwesome
                    name="inbox"
                  size={Platform.select({ web: 20, default: 20 })}
                    color={Platform.select({ web: "rgb(90,192,234)", native: "white" })}
                  />
                </View>
              </Badge>
              {menuOpen && Platform.OS === 'web' && (
                <Text style={[
                  styles.staticMenuText,
                  theme ? styles.staticMenuTextLight : styles.staticMenuTextDark
                ]}>
                  {t('Emails')}
                </Text>
              )}
            </View>
          </Pressable>
          <Pressable
            onPress={handleStoreMenuOpen}
            onHoverIn={() => { animation2.current?.reset(), animation2.current?.play() }}
            style={({ pressed, hovered }) => ([
              styles.centerEverything,
              styles.staticMenuPressable,
              menuOpen ? styles.staticMenuPressableOpen : styles.staticMenuPressableClosed,
              (pressed || hovered) && (theme ? styles.staticMenuPressableHoveredLight : styles.staticMenuPressableHoveredDark),
              { overflow: 'visible' },
              Platform.select({
                ios: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 4,
                },
                android: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                },
              })
            ])}
          >
          <View
            ref={storeButtonRef}
            onLayout={() => {
              // Measure on layout as backup (especially useful on first render)
              if (Platform.OS !== 'web' && storeButtonRef.current && storeMenuAnchor) {
                requestAnimationFrame(() => {
                  if (storeButtonRef.current) {
                    try {
                      storeButtonRef.current.measureInWindow((x, y, width, height) => {
                        if (x !== undefined && y !== undefined) {
                          setStoreButtonLayout({ x, y, width: width || 0, height: height || 0 });
                        }
                      });
                    } catch (error) {
                      // Silently fail - we have fallback positioning
                    }
                  }
                });
              }
            }}
            style={[
              styles.staticMenuIconContainer,
            ]}
          >
              <View style={[
                styles.staticMenuIconWrapper,
                theme ? styles.staticMenuIconWrapperLight : styles.staticMenuIconWrapperDark,
                Platform.select({
                  ios: { padding: 10 },
                  android: { padding: 10 },
                })
              ]}>
                <LottieView
                  key={2}
                  autoPlay
                  ref={animation2}
                  loop={false}
                  style={{
                    minWidth: Platform.select({ web: 60, default: 52 }),
                    height: Platform.select({ web: 60, default: 52 }),
                    alignSelf: 'center'
                  }}
                  source={require('@assets/animations/78018-store-icon.json')}
                />
              </View>
              {menuOpen && Platform.OS === 'web' && (
                <Text style={[
                  styles.staticMenuText,
                  theme ? styles.staticMenuTextLight : styles.staticMenuTextDark
                ]}>
                  {t('Store')}
                </Text>
              )}
            </View>
          </Pressable>

          {/* Store Menu Dropdown */}
        {StoreMenuDropdown}

          <Pressable
          onPress={() => handleNavigationWithOnboardingCheck(() => navigation.replace('Invitations'), 'Invitations')}
            onHoverIn={() => { animation2.current?.reset(), animation2.current?.play() }}
            style={({ pressed, hovered }) => ([
              styles.centerEverything,
              styles.staticMenuPressable,
              menuOpen ? styles.staticMenuPressableOpen : styles.staticMenuPressableClosed,
              (pressed || hovered) && (theme ? styles.staticMenuPressableHoveredLight : styles.staticMenuPressableHoveredDark),
              Platform.select({
                ios: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 4,
                },
                android: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.15)',
                },
              }),
            ])}
          >
            <View style={[
            styles.staticMenuIconContainer,
            ]}>
              <Badge color="error" showZero={true} max={999} badgeContent={invitationCount}>
                <View style={[
                  styles.staticMenuIconWrapper,
                  theme ? styles.staticMenuIconWrapperLight : styles.staticMenuIconWrapperDark,
                  Platform.select({
                    ios: { padding: 10 },
                    android: { padding: 10 },
                  })
                ]}>
                  <AntDesign
                    name="mail"
                  size={Platform.select({ web: 20, default: 20 })}
                    color={Platform.select({ web: "rgb(90,192,234)", native: "white" })}
                  />
                </View>
              </Badge>
              {menuOpen && Platform.OS === 'web' && (
                <Text style={[
                  styles.staticMenuText,
                  theme ? styles.staticMenuTextLight : styles.staticMenuTextDark
                ]}>
                  {t('Invites')}
                </Text>
              )}
            </View>
          </Pressable>
          <Pressable
            onPress={() => handleNavigationWithOnboardingCheck(() => navigation.replace('Academy'), 'Academy')}
            onHoverIn={() => { animation2.current?.reset(), animation2.current?.play() }}
            style={({ pressed, hovered }) => ([
              styles.centerEverything,
              styles.staticMenuPressable,
              menuOpen ? styles.staticMenuPressableOpen : styles.staticMenuPressableClosed,
              (pressed || hovered) && (theme ? styles.staticMenuPressableHoveredLight : styles.staticMenuPressableHoveredDark),
              Platform.select({
                ios: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 4,
                },
                android: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                },
              }),
            ])}
          >
            <View style={[
            styles.staticMenuIconContainer,
            ]}>
              <View style={[
                styles.staticMenuIconWrapper,
                theme ? styles.staticMenuIconWrapperLight : styles.staticMenuIconWrapperDark,
                Platform.select({
                  ios: { padding: 10 },
                  android: { padding: 10 },
                })
              ]}>
                <LottieView
                  key={2}
                  autoPlay
                  loop={false}
                  style={{
                    width: Platform.select({ web: 40, default: 32 }),
                    height: Platform.select({ web: 40, default: 32 }),
                    alignSelf: 'center'
                  }}
                  source={require('@assets/animations/graduation.json')}
                />
              </View>
              {menuOpen && Platform.OS === 'web' && (
                <Text style={[
                  styles.staticMenuText,
                  theme ? styles.staticMenuTextLight : styles.staticMenuTextDark
                ]}>
                  {t('Academy')}
                </Text>
              )}
            </View>
          </Pressable>
          <Pressable
          onPress={handleMarketplacePress}
            onHoverIn={() => { animation2.current?.reset(), animation2.current?.play() }}
            style={({ pressed, hovered }) => ([
              styles.centerEverything,
              styles.staticMenuPressable,
              menuOpen ? styles.staticMenuPressableOpen : styles.staticMenuPressableClosed,
              (pressed || hovered) && (theme ? styles.staticMenuPressableHoveredLight : styles.staticMenuPressableHoveredDark),
              Platform.select({
                ios: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 4,
                },
                android: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                },
              }),
            ])}
          >
            <View style={[
            styles.staticMenuIconContainer,
            ]}>
              <View style={[
                styles.staticMenuIconWrapper,
                theme ? styles.staticMenuIconWrapperLight : styles.staticMenuIconWrapperDark,
                Platform.select({
                  ios: { padding: 10 },
                  android: { padding: 10 },
                })
              ]}>
                <MaterialIcons
                  name="shopping-cart"
                size={Platform.select({ web: 20, default: 20 })}
                  color={Platform.select({ web: "rgb(90,192,234)", native: "white" })}
                />
              </View>
              {menuOpen && Platform.OS === 'web' && (
                <Text style={[
                  styles.staticMenuText,
                  theme ? styles.staticMenuTextLight : styles.staticMenuTextDark
                ]}>
                  {t('Marketplace')}
                </Text>
              )}
            </View>
          </Pressable>
          <Pressable
          onPress={() => handleNavigationWithOnboardingCheck(() => navigation.replace("Settings" as any, { screen: "Upgrade" }), 'Upgrade')}
            onHoverIn={() => { animation2.current?.reset(), animation2.current?.play() }}
            style={({ pressed, hovered }) => ([
              styles.centerEverything,
              styles.staticMenuPressable,
              menuOpen ? styles.staticMenuPressableOpen : styles.staticMenuPressableClosed,
              (pressed || hovered) && (theme ? styles.staticMenuPressableHoveredLight : styles.staticMenuPressableHoveredDark),
              Platform.select({
                ios: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 4,
                },
                android: {
                  padding: 12,
                  marginVertical: 4,
                  borderRadius: 12,
                  backgroundColor: theme ? 'rgba(255,255,255,0.7)' : 'rgb(51,51,51)',
                },
              }),
            ])}
          >
            <View style={[
            styles.staticMenuIconContainer,
            ]}>
            <View style={{ position: 'relative' }}>
              <View style={[
                styles.staticMenuIconWrapper,
                theme ? styles.staticMenuIconWrapperLight : styles.staticMenuIconWrapperDark,
                Platform.select({
                  ios: { padding: 10 },
                  android: { padding: 10 },
                })
              ]}>
                <LottieView
                  key={2}
                  autoPlay
                  loop={false}
                  style={{
                    width: Platform.select({ web: 40, default: 32 }),
                    height: Platform.select({ web: 40, default: 32 }),
                    alignSelf: 'center'
                  }}
                  source={require('@assets/animations/111742-diamond-upgrade-reward-animation.json')}
                />
              </View>
              {isInTrial && isPremiumUser() && (
                <View style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  backgroundColor: '#ff9800',
                  borderRadius: 6,
                  paddingHorizontal: 4,
                  paddingVertical: 1,
                  minWidth: 28,
                  height: 14,
                  alignItems: 'center',
                  justifyContent: 'center',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 1 },
                  shadowOpacity: 0.25,
                  shadowRadius: 2,
                  elevation: 3,
                  borderWidth: 1,
                  borderColor: 'white',
                  zIndex: 10,
                }}>
                  <Text style={{
                    color: 'white',
                    fontSize: 7,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    lineHeight: 12,
                    left: -1,
                    top: -2
                  }}>
                    TRIAL
                  </Text>
                </View>
              )}
            </View>
              {menuOpen && Platform.OS === 'web' && (
              <Text adjustsFontSizeToFit style={[
                  styles.staticMenuText,
                theme ? styles.staticMenuTextLight : styles.staticMenuTextDark, { maxWidth: 100 }
                ]}>
                {isPremiumUser()
                  ? `${(subscription.productName || 'Premium').split(' ')[0]}${isInTrial ? ' (Trial)' : ''}`
                  : t('Upgrade')}
                </Text>
              )}
            </View>
          </Pressable>

          {/* Development-only API URL Configuration Button */}
          {/* {__DEV__ && (
            <Pressable
              onPress={handleOpenDevApiModal}
              onHoverIn={() => { animation2.current?.reset(), animation2.current?.play() }}
              style={({ pressed, hovered }) => ([
                styles.centerEverything,
                styles.staticMenuPressable,
                menuOpen ? styles.staticMenuPressableOpen : styles.staticMenuPressableClosed,
                (pressed || hovered) && (theme ? styles.staticMenuPressableHoveredLight : styles.staticMenuPressableHoveredDark),
                Platform.select({
                  ios: {
                    padding: 12,
                    marginVertical: 4,
                    borderRadius: 12,
                    backgroundColor: theme ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.15)',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.15,
                    shadowRadius: 4,
                  },
                  android: {
                    padding: 12,
                    marginVertical: 4,
                    borderRadius: 12,
                    backgroundColor: theme ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.15)',
                    elevation: 3,
                  },
                }),
              ])}
            >
              <View style={[
                styles.staticMenuIconContainer,
              ]}>
                <View style={[
                  styles.staticMenuIconWrapper,
                  theme ? styles.staticMenuIconWrapperLight : styles.staticMenuIconWrapperDark,
                  Platform.select({
                    ios: { padding: 10 },
                    android: { padding: 10 },
                  })
                ]}>
                  <MaterialIcons
                    name="settings"
                    size={Platform.select({ web: 20, default: 24 })}
                    color="rgb(255,193,7)"
                  />
                </View>
                {menuOpen && Platform.OS === 'web' && (
                  <Text style={[
                    styles.staticMenuText,
                    theme ? styles.staticMenuTextLight : styles.staticMenuTextDark
                  ]}>
                    🔧 Dev API
                  </Text>
                )}
              </View>
            </Pressable>
          )} */}
        </XStack>

        {/* Development API URL Modal */}
        {DevApiUrlModal()}

      {/* Coming Soon Modal for Academy */}
      <ComingSoonModal
        visible={academyModalState.visible}
        onClose={hideAcademyModal}
        title={academyModalState.title}
        subtitle={academyModalState.subtitle}
        message={academyModalState.message}
        showAnimation={academyModalState.showAnimation}
        closeOnBackdrop={academyModalState.closeOnBackdrop}
        showCloseButton={academyModalState.showCloseButton}
      />
    </View>
  );
});

export default StaticMenuHeader;